import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calculator, ShoppingCart, Package, FileX, FileText, FileSpreadsheet, ArrowRight, Save, IndianRupee, ArrowLeft } from 'lucide-react';
import { Tabs } from '../../../components/ui';
import { Button } from '../../../components/ui/Button';

import { ResizableSplitPane } from '../components/ResizableSplitPane';
import { BudgetSearchFilter } from '../components/orderingtSearchFilter';
import { PdfViewer } from '../components/pdfviewer';
import { XlViewer } from '../components/xl_viewer';
import { InitialQuotationMaker } from '../components/ordering_quotation_maker';
import { QuotationPreviewModal } from '../components/ordering_quotation_preview';
import { OrderingTableView } from '../components/ordering_table_view';
import { QuotationPrepScreen } from './QuotationPrepScreen';

import { documentService } from '../../../services/documentService';
import { fetchPurchases } from '../../inventory/pages/InventoryPage/services';
import { API_BASE_URL } from '../../../services/api';
import { format } from 'date-fns';
import { orderingQuotationService } from '../services/orderingQuotationService';
import { showSuccessMessage, showAxiosError } from '../../../utils/toast';
import { useAuthStore } from "@/store/authStore";

const MAIN_TABS = [
  { id: 'ordering', label: 'Master Order', icon: ShoppingCart },
  { id: 'inventory', label: 'Order Prep', icon: Calculator }
];

const INVENTORY_TABS = [];

const ORDERING_TABS = [];

const resolveContactRef = (contactObj, stateObj) => {
  const contact = contactObj || stateObj || {};
  const providedRef = stateObj?.contactRef || contact.contactRef;
  if (providedRef && typeof providedRef === 'string' && !/^[0-9a-fA-F]{24}$/.test(providedRef) && !/^ver-/i.test(providedRef) && !/^vk/i.test(providedRef) && providedRef !== contact.customId && providedRef !== contact.verveId && providedRef !== String(contact._id)) {
    return providedRef;
  }
  const refSource = contact.referenceType || contact.leadId?.referenceType || (contact.source && contact.source !== 'Manual' ? contact.source : '') || '';
  if (refSource) return refSource;
  return contact.referenceType || contact.source || '-';
};

const OrderingScreen = ({ embedded = false, initialData = null, onBack = null, onSuccess = null }) => {
  const { user } = useAuthStore();
  const loggedInUserName = user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '';
  const location = useLocation();
  const navigate = useNavigate();
  const stateData = initialData || location.state;
  const returnToExecutionId = location.state?.returnToExecutionId || stateData?.returnToExecutionId;

  const [activeMainTab, setActiveMainTab] = useState(() => {
    if (embedded && stateData) return 'inventory';
    return window.location.pathname.includes('/create') ? 'inventory' : 'ordering';
  });
  const [activeInventoryTab, setActiveInventoryTab] = useState('catalog');
  const [activeOrderingTab, setActiveOrderingTab] = useState('appliances');

  const [customTableTotal, setCustomTableTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("Catalog");
  const [stockSelections, setStockSelections] = useState({});

  const [catalogDocs, setCatalogDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [quotationDetails, setQuotationDetails] = useState({
    projectName: '', site: '', contactRef: '', date: new Date().toLocaleDateString('en-GB'), quotationNo: '', referenceDesigner: '', category: 'Appliances',
    docType: location.state?.docType || 'Order'
  });

  const [quotationTableData, setQuotationTableData] = useState({
    rows: [], subTotal: 0, discountAmount: 0, gstAmount: 0, grandTotal: 0,
    discountType: 'percentage', discountValue: 0, gstType: 'percentage', gstValue: 0
  });

  const [showPreview, setShowPreview] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    if (stateData?.createNewQuotationForLead) {
      const lead = stateData.createNewQuotationForLead;
      const leadId = stateData.leadId || lead._id || lead.id || lead.customId;
      const customerName = lead.customerName || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.projectName || '';
      const siteStr = `${lead.city || ''}, ${lead.state || ''}`.replace(/^,\s*|,\s*$/g, '') || lead.addressLine1 || lead.site || '';

      setQuotationTableData({
        rows: [], subTotal: 0, discountAmount: 0, gstAmount: 0, grandTotal: 0,
        discountType: 'percentage', discountValue: 0, gstType: 'percentage', gstValue: 0
      });
      setCustomTableTotal(0);

      setQuotationDetails({
        id: null,
        projectName: customerName,
        site: siteStr,
        contactRef: resolveContactRef(lead.contactObj || lead, stateData),
        leadId: leadId,
        clientId: leadId,
        date: new Date().toLocaleDateString('en-GB'),
        quotationNo: '',
        referenceDesigner: loggedInUserName || lead.referenceDesigner || lead.designer || '',
        category: 'Appliances',
        docType: location.state?.docType || 'Order'
      });
      setActiveMainTab('inventory');
      if (!embedded) window.history.replaceState({}, document.title);
    } else if (stateData?.editQuotation) {
      handleEditQuotation(stateData.editQuotation);
      if (!embedded) window.history.replaceState({}, document.title);
    } else if (stateData) {
      const contact = stateData.contactObj || (typeof stateData.leadId === 'object' && stateData.leadId ? stateData.leadId : null) || (typeof stateData.clientId === 'object' && stateData.clientId ? stateData.clientId : null) || stateData;
      const customerName = stateData.projectName || stateData.clientName || contact.customerName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || '';
      const phone = contact.phoneNumber || contact.phone || contact.mobile || stateData.phoneNumber || stateData.phone || '';
      const nameWithPhone = phone && !customerName.includes(phone) ? `${customerName} - ${phone}` : customerName;
      const siteStr = stateData.site || `${contact.city || ''}, ${contact.state || ''}`.replace(/^,\s*|,\s*$/g, '') || contact.addressLine1 || contact.site || '';
      const contactRefStr = resolveContactRef(contact, stateData);
      const designerStr = stateData.referenceDesigner || stateData.designer || contact.referenceDesigner || contact.designer || '';

      const isExistingQuote = Boolean(
        stateData.isExistingOrderUpdate ||
        (stateData.editQuotation && (stateData.editQuotation._id || stateData.editQuotation.quotationNo))
      );

      if (isExistingQuote) {
        const loadExisting = (dataObj) => {
          const c = dataObj.contactObj || (typeof dataObj.leadId === 'object' && dataObj.leadId ? dataObj.leadId : null) || (typeof dataObj.clientId === 'object' && dataObj.clientId ? dataObj.clientId : null) || contact;
          const cName = dataObj.projectName || dataObj.clientName || c.customerName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || customerName;
          const ph = c.phoneNumber || c.phone || c.mobile || dataObj.phoneNumber || dataObj.phone || phone;
          const cNameWithPhone = ph && !cName.includes(ph) ? `${cName} - ${ph}` : cName;
          const sStr = dataObj.site || `${c.city || ''}, ${c.state || ''}`.replace(/^,\s*|,\s*$/g, '') || c.addressLine1 || c.site || siteStr;
          const cRefStr = resolveContactRef(c, dataObj);
          const desStr = dataObj.referenceDesigner || dataObj.designer || c.referenceDesigner || c.designer || designerStr || loggedInUserName;

          const dt = dataObj.totals?.discountType || dataObj.discountType || 'percentage';
          const dv = dataObj.totals?.discountValue !== undefined && dataObj.totals.discountValue !== null
            ? dataObj.totals.discountValue
            : (dataObj.discountValue !== undefined && dataObj.discountValue !== null ? dataObj.discountValue : 0);
          const gt = dataObj.totals?.gstType || dataObj.gstType || 'percentage';
          const gv = dataObj.totals?.gstValue !== undefined && dataObj.totals.gstValue !== null
            ? dataObj.totals.gstValue
            : (dataObj.gstValue !== undefined && dataObj.gstValue !== null ? dataObj.gstValue : 0);

          setQuotationDetails({
            id: dataObj._id || dataObj.id || stateData._id || stateData.id,
            projectName: cNameWithPhone,
            site: sStr,
            contactRef: cRefStr,
            date: dataObj.date || new Date().toLocaleDateString('en-GB'),
            quotationNo: dataObj.quotationNo || stateData.quotationNo,
            referenceDesigner: desStr,
            category: dataObj.category || 'Appliances',
            docType: dataObj.docType || location.state?.docType || 'Order',
            leadId: c._id || dataObj.leadId || dataObj._id || stateData._id,
            clientId: c._id || dataObj.clientId || dataObj._id || stateData._id,
            isConfirmOrder: dataObj.isConfirmOrder ?? dataObj.isConformOrder ?? false,
            isConformOrder: dataObj.isConformOrder ?? dataObj.isConfirmOrder ?? false,
            salesType: dataObj.salesType || c.salesType || 'Project Sale',
            isDirectSale: dataObj.isDirectSale || c.isDirectSale || false
          });
          setQuotationTableData({
            rows: dataObj.rows || [],
            subTotal: dataObj.totals?.subTotal ?? dataObj.subTotal ?? 0,
            discountAmount: dataObj.totals?.discountAmount ?? dataObj.discountAmount ?? 0,
            gstAmount: dataObj.totals?.gstAmount ?? dataObj.gstAmount ?? 0,
            grandTotal: dataObj.totals?.grandTotal ?? dataObj.grandTotal ?? 0,
            discountType: dt,
            discountValue: dv,
            gstType: gt,
            gstValue: gv
          });
          setCustomTableTotal(dataObj.totals?.grandTotal ?? dataObj.totals?.subTotal ?? 0);
        };

        if (Array.isArray(stateData.rows) && stateData.rows.length > 0) {
          loadExisting(stateData);
        } else {
          loadExisting(stateData);
        }

        if (stateData._id || stateData.id) {
          orderingQuotationService.getQuotationById(stateData._id || stateData.id)
            .then(res => {
              if (res && res.success && res.data) {
                loadExisting(res.data);
              }
            })
            .catch(err => console.error('Failed to load full quotation details:', err));
        }
      } else {
        const isStubDraft = Boolean(stateData._id && (!stateData.quotationNo || stateData.quotationNo === '-'));
        const targetId = isStubDraft ? stateData._id : null;

        setQuotationDetails(prev => ({
          ...prev,
          id: targetId,
          projectName: nameWithPhone || prev.projectName,
          site: siteStr || prev.site,
          contactRef: contactRefStr || prev.contactRef,
          referenceDesigner: designerStr || loggedInUserName || prev.referenceDesigner,
          date: prev.date || new Date().toLocaleDateString('en-GB'),
          quotationNo: isStubDraft ? (prev.quotationNo || '') : '',
          docType: location.state?.docType || prev.docType || 'Order',
          leadId: contact._id || (isStubDraft ? stateData.leadId : (stateData._id || prev.leadId)),
          clientId: contact._id || (isStubDraft ? stateData.clientId : (stateData._id || prev.clientId))
        }));

        setQuotationTableData({
          rows: [], subTotal: 0, discountAmount: 0, gstAmount: 0, grandTotal: 0,
          discountType: 'percentage', discountValue: 0, gstType: 'percentage', gstValue: 0
        });
      }
      setActiveMainTab('inventory');
      if (!embedded) window.history.replaceState({}, document.title);
    }
  }, [stateData, embedded]);

  useEffect(() => {
    // If quotationNo is missing (whether new quote or draft without number)
    if (!quotationDetails.quotationNo) {
      orderingQuotationService.getNextQuotationNo(quotationDetails.category || 'Appliances')
        .then(res => {
          if (res && res.data && res.data.quotationNo) {
            setQuotationDetails(prev => ({
              ...prev,
              quotationNo: res.data.quotationNo,
              date: prev.date || new Date().toLocaleDateString('en-GB')
            }));
          } else {
            const catStr = quotationDetails.category || 'Appliances';
            const letter = catStr === 'Transport' ? 'H' : catStr.charAt(0).toUpperCase();
            const year = new Date().getFullYear();
            setQuotationDetails(prev => ({
              ...prev,
              quotationNo: prev.quotationNo || `VK${year}${letter}00001`,
              date: prev.date || new Date().toLocaleDateString('en-GB')
            }));
          }
        })
        .catch(err => {
          console.error("Failed to fetch next ordering quotation no:", err);
          const catStr = quotationDetails.category || 'Appliances';
          const letter = catStr === 'Transport' ? 'H' : catStr.charAt(0).toUpperCase();
          const year = new Date().getFullYear();
          setQuotationDetails(prev => ({
            ...prev,
            quotationNo: prev.quotationNo || `VK${year}${letter}00001`,
            date: prev.date || new Date().toLocaleDateString('en-GB')
          }));
        });
      } else if (quotationDetails.quotationNo.startsWith('VK')) {
        // If the user switches category (e.g. Appliances to Transport),
        // replace the category letter (7th char in VKYYYYXNNNNN format) without consuming another sequence!
        const currentNo = quotationDetails.quotationNo;
        const catStr = quotationDetails.category || 'Appliances';
        const catLetter = catStr === 'Transport' ? 'H' : catStr.charAt(0).toUpperCase();
        if (currentNo.length === 12 && currentNo.charAt(6) !== catLetter) {
          const updatedNo = currentNo.slice(0, 6) + catLetter + currentNo.slice(7);
          setQuotationDetails(prev => ({ ...prev, quotationNo: updatedNo }));
        }
      }
  }, [quotationDetails.category, quotationDetails.quotationNo]);

  useEffect(() => {
    if (activeMainTab !== 'inventory') return;
    const fetchCatalogDocs = async () => {
      if (filterType === 'Stock') {
        setLoadingDocs(true);
        try {
          const res = await fetchPurchases();
          const pList = res?.data?.data || res?.data || (Array.isArray(res) ? res : []);
          const filtered = Array.isArray(pList) ? pList.filter(item => {
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            const vendorName = item.vendorName || (typeof item.vendor === 'object' ? (item.vendor?.vendorName || item.vendor?.name) : item.vendor) || '';
            const itemName = item.item?.itemName || item.item?.name || item.description || '';
            const invoiceNo = item.invoiceNumber || item.invoiceNo || '';
            return vendorName.toLowerCase().includes(q) || itemName.toLowerCase().includes(q) || invoiceNo.toLowerCase().includes(q);
          }) : [];
          setCatalogDocs(filtered);
        } catch (err) {
          console.error("Failed to fetch purchases:", err);
          setCatalogDocs([]);
        } finally {
          setLoadingDocs(false);
        }
        return;
      }
      
      setLoadingDocs(true);
      try {
        const response = await documentService.getDocuments({
          isRelatedToCatalog: true,
          category: 'Ordering',
          search: searchQuery,
          formatFilter: filterType === 'Catalog' ? 'All' : filterType,
        });
        if (response.success) {
          setCatalogDocs(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch catalog documents:", error);
      } finally {
        setLoadingDocs(false);
      }
    };
    fetchCatalogDocs();
  }, [searchQuery, filterType, activeMainTab]);

  const handleCancelQuotation = () => {
    navigate('/ordering');
  };

  const handleEditQuotation = (row) => {
    setQuotationDetails({
      id: row._id,
      projectName: row.projectName || '',
      site: row.site || '',
      contactRef: row.contactObj?.customId || row.contactRef || row.leadId || row.clientId || '',
      leadId: row.leadId || row.contactRef || null,
      clientId: row.clientId || row.contactRef || null,
      date: row.date || '',
      quotationNo: row.quotationNo || '',
      referenceDesigner: row.referenceDesigner || '',
      category: row.category || 'Appliances',
      docType: row.docType || 'Order',
      isConfirmOrder: row.isConfirmOrder ?? row.isConformOrder ?? true,
      isConformOrder: row.isConformOrder ?? row.isConfirmOrder ?? true
    });
    setQuotationTableData({
      rows: row.rows || [],
      subTotal: row.totals?.subTotal || 0,
      discountAmount: row.totals?.discountAmount || 0,
      gstAmount: row.totals?.gstAmount || 0,
      grandTotal: row.totals?.grandTotal || 0,
      discountType: row.totals?.discountType || 'percentage',
      discountValue: row.totals?.discountValue || 0,
      discountLabel: row.totals?.discountLabel || '',
      gstType: row.totals?.gstType || 'percentage',
      gstValue: row.totals?.gstValue || 0,
      gstLabel: row.totals?.gstLabel || '',
    });
    setActiveMainTab('inventory');
  };



  const handleSaveQuotation = async (pdfBlob = null, sendToOwner = false, paymentData = null, isConfirmOrder = false) => {
    const rows = quotationTableData.rows;
    const hasValidItem = rows.some(r => {
      const name = (r.text || r.particulars || '').trim();
      const qty = Number(r.quantity);
      const amt = Number(r.amount);
      return name && qty > 0 && amt > 0;
    });
    if (!hasValidItem) {
      showAxiosError(new Error('Add at least one item with a name, quantity > 0, and amount > 0.'));
      return;
    }
    try {
      const payload = {
        ...quotationDetails,
        sendToOwner,
        isConfirmOrder: Boolean(isConfirmOrder),
        isConformOrder: Boolean(isConfirmOrder),
        rows: quotationTableData.rows.filter(r => {
          const name = (r.text || r.particulars || '').trim();
          const qty = Number(r.quantity);
          const amt = Number(r.amount);
          return name && qty > 0 && amt > 0;
        }),
        totals: {
          subTotal: quotationTableData.subTotal,
          discountAmount: quotationTableData.discountAmount,
          gstAmount: quotationTableData.gstAmount,
          grandTotal: quotationTableData.grandTotal,
          discountType: quotationTableData.discountType,
          discountValue: quotationTableData.discountValue,
          discountLabel: quotationTableData.discountValue && Number(quotationTableData.discountValue) !== 0 ? (quotationTableData.discountType === 'percentage' ? `(${quotationTableData.discountValue}%)` : `(₹${Number(quotationTableData.discountValue).toLocaleString('en-IN')})`) : '',
          gstType: quotationTableData.gstType,
          gstValue: quotationTableData.gstValue,
          gstLabel: quotationTableData.gstValue && Number(quotationTableData.gstValue) !== 0 ? (quotationTableData.gstType === 'percentage' ? `(${quotationTableData.gstValue}%)` : `(₹${Number(quotationTableData.gstValue).toLocaleString('en-IN')})`) : ''
        },
        // Include payment details recorded by the user at submission time
        paymentData: paymentData || null
      };

      const formData = new FormData();
      formData.append('quotationData', JSON.stringify(payload));
      if (pdfBlob) {
        formData.append('pdfFile', pdfBlob, `Quotation_${quotationDetails.quotationNo || Date.now()}.pdf`);
      }

      const response = await orderingQuotationService.saveQuotation(formData);
      if (response.success) {
        setShowPreview(false);
        setShowSuccessModal(true);
        if (pdfBlob && !Boolean(isConfirmOrder)) {
          const url = window.URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Ordering_Quotation_${quotationDetails.quotationNo || Date.now()}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }
      } else {
        showAxiosError(new Error('Failed to save quotation. Please try re-uploading.'));
      }
    } catch (error) {
      console.error(error);
      showAxiosError(error, 'Error occurred while saving. Please try re-uploading.');
    }
  };

  const totalBudget = customTableTotal;

  const renderInventoryTabContent = () => (
    <QuotationPrepScreen
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filterType={filterType}
      setFilterType={setFilterType}
      loadingDocs={loadingDocs}
      catalogDocs={catalogDocs}
      stockSelections={stockSelections}
      setStockSelections={setStockSelections}
      selectedDocument={selectedDocument}
      setSelectedDocument={setSelectedDocument}
      quotationDetails={quotationDetails}
      setQuotationDetails={setQuotationDetails}
      quotationTableData={quotationTableData}
      setQuotationTableData={setQuotationTableData}
      customTableTotal={customTableTotal}
      setCustomTableTotal={setCustomTableTotal}
      showPreview={showPreview}
      setShowPreview={setShowPreview}
      handleSaveQuotation={handleSaveQuotation}
      handleCancelQuotation={handleCancelQuotation}
    />
  );

  return (
    <div className="space-y-6 pb-10 animate-in fade-in duration-500">
      <div className="flex flex-col gap-2">
        {!embedded && (
          <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between shadow-sm mb-2">
            <div className="pl-8 z-20">
              <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">
                Order Management
              </h1>
              <p className="text-sm font-body text-slate-400 mt-2 font-medium">Verve Kitchens Order Management</p>
            </div>
            <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
              <img
                src="/kitchens/1.jpg"
                alt="Order Management"
                className="absolute top-0 right-0 w-full h-full object-cover object-center opacity-90"
              />
            </div>
          </div>
        )}

        {/* Main Tabs commented out to enforce workflow: Master Order -> Workspace -> New Quotation */}
        {/* <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-200 overflow-hidden">
          <Tabs
            tabs={MAIN_TABS}
            activeTab={activeMainTab}
            onChange={setActiveMainTab}
            variant="main"
            className="w-full !border-b-0"
          />
        </div> */}

        {/* Sub Tabs and Content */}
        <div className="flex flex-col mt-1">
          {(activeMainTab === 'inventory' ? INVENTORY_TABS : ORDERING_TABS).length > 0 && (
            <Tabs
              tabs={activeMainTab === 'inventory' ? INVENTORY_TABS : ORDERING_TABS}
              activeTab={activeMainTab === 'inventory' ? activeInventoryTab : activeOrderingTab}
              onChange={activeMainTab === 'inventory' ? setActiveInventoryTab : setActiveOrderingTab}
              variant="sub"
              className="mb-2"
            />
          )}

          <div className="min-h-[400px]">
            {activeMainTab === 'inventory' ? (
              <div className="flex flex-col h-full">
                {/* Standalone Top Navigation Bar exactly like LeadOrderingWorkspace */}
                {/* {!embedded && (
                  <div className="flex items-center justify-between mb-4">
                    <button
                      type="button"
                      onClick={handleCancelQuotation}
                      className="inline-flex items-center gap-2 px-3.5 py-2 bg-white hover:bg-slate-100 text-slate-700 rounded-xl border border-slate-200/80 shadow-sm font-medium text-sm transition-all cursor-pointer"
                    >
                      <ArrowLeft size={16} />
                      <span>Back to Master Orders</span>
                    </button>
                  </div>
                )} */}
                {renderInventoryTabContent()}
              </div>
            ) : (
              <OrderingTableView activeTab={activeOrderingTab} onEdit={handleEditQuotation} onAddOrder={() => setActiveMainTab('inventory')} />
            )}
          </div>
          {showSuccessModal && (
            <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full flex flex-col items-center text-center animate-in zoom-in duration-300">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                  <svg className="w-10 h-10 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-2">Success!</h3>
                <p className="text-slate-600 mb-8 text-base">
                  Your quotation has been successfully generated and saved to the database.
                </p>
                <Button
                  variant="primary"
                  className="w-full h-12 text-lg !bg-emerald-600 hover:!bg-emerald-700 !border-emerald-600 !text-white"
                  onClick={() => {
                    setShowSuccessModal(false);
                    if (onSuccess) {
                      onSuccess();
                    } else if (onBack) {
                      onBack();
                    } else if (returnToExecutionId) {
                      navigate(`/lead-executions/${returnToExecutionId}`);
                    } else {
                      navigate('/budget');
                    }
                  }}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderingScreen;

